import React, { useState, useRef } from "react";
import {
  Button,
  ButtonProps,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";

export interface DropdownOption {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

type DropdownProps = Omit<ButtonProps, "onChange"> & {
  options: DropdownOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  showArrow?: boolean;
  menuProps?: {
    maxHeight?: number;
    minWidth?: number;
    anchorOrigin?: {
      vertical: "top" | "bottom";
      horizontal: "left" | "right";
    };
    transformOrigin?: {
      vertical: "top" | "bottom";
      horizontal: "left" | "right";
    };
  };
};

export const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  fullWidth = false,
  variant = "outlined",
  size = "medium",
  showArrow = true,
  menuProps = {},
  sx,
  ...buttonProps
}: DropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (optionValue: string | number) => {
    if (onChange) {
      onChange(optionValue);
    }
    handleClose();
  };

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const {
    maxHeight = 300,
    minWidth = 200,
    anchorOrigin = { vertical: "bottom", horizontal: "left" },
    transformOrigin = { vertical: "top", horizontal: "left" },
  } = menuProps;

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={handleClick}
        disabled={disabled}
        fullWidth={fullWidth}
        variant={variant}
        size={size}
        endIcon={showArrow ? <KeyboardArrowDown /> : undefined}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          ...sx,
        }}
        {...buttonProps}
      >
        {displayText}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{
          style: {
            maxHeight,
            minWidth,
          },
        }}
        MenuListProps={{
          "aria-labelledby": "dropdown-button",
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleOptionClick(option.value)}
            disabled={option.disabled}
            selected={option.value === value}
            sx={{
              py: 1.5,
              px: 2,
              my: 0.5,
            }}
          >
            {option.icon && (
              <ListItemIcon sx={{ minWidth: 36 }}>{option.icon}</ListItemIcon>
            )}
            <ListItemText
              primary={option.label}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
